'use client';

import * as ToastPrimitive from '@radix-ui/react-toast';
import { DynamicIcon } from 'phosphor-icons/react';
import * as React from 'react';
import { useState } from 'react';
import { c } from '../../../utils/tailwind-merge';
import { Button } from '../../atoms/Button';
import { RichText } from '../RichText';
import { SnackBarProps } from './SnackBar.types';

export const SnackBar = ({
  microcopy,
  icon,
  iconWeight,
  actionIcon,
  closeIcon,
  duration = 3000,
  onClick,
  onActionClick,
  onCloseIconClick,
  onClose,
  className = '',
}: SnackBarProps) => {
  const [open, setOpen] = useState(true);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      onClose();
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleActionTextClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    if (onActionClick) {
      onActionClick();
    }
  };

  const handleCloseClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (onCloseIconClick) {
      onCloseIconClick();
    } else {
      onClose();
    }
    setOpen(false);
  };

  return (
    <ToastPrimitive.Provider swipeDirection="right" duration={duration}>
      <ToastPrimitive.Root
        data-slot="snack-bar"
        open={open}
        onOpenChange={handleOpenChange}
        className={c(
          'pointer-events-auto flex items-center overflow-hidden rounded-lg',
          'h-12 border border-grey-800 bg-grey-900 text-white shadow-card md:h-14',
          className
        )}
        onClick={handleClick}
      >
        <div
          className={c(
            'px-4',
            'pointer-events-auto flex items-center',
            'gap-2 text-base leading-6 tracking-tight'
          )}
        >
          {icon ? (
            <DynamicIcon
              iconName={icon}
              size={24}
              weight={iconWeight}
              aria-hidden="true"
              focusable="false"
              className="hidden flex-shrink-0 text-white md:inline-flex"
            />
          ) : null}
          <div className="flex items-center gap-[2px]">
            <ToastPrimitive.Title>
              <RichText
                html={microcopy.title}
                type="span"
                className="text-base font-normal leading-6 tracking-tight text-white"
              />
            </ToastPrimitive.Title>
            {(microcopy.actionLabel || actionIcon) && (
              <ToastPrimitive.Action
                asChild
                altText={
                  microcopy.actionAriaLabel || microcopy.actionLabel || 'Action'
                }
              >
                <Button.v2
                  onClick={handleActionTextClick}
                  variant="tertiary-blue-monochrome"
                  size="xsmall"
                  shape="square"
                  {...(microcopy.actionAriaLabel && {
                    'aria-label': microcopy.actionAriaLabel,
                  })}
                  iconRight={actionIcon}
                  iconClassName={c(
                    'text-blue !h-5 !w-5',
                    microcopy.actionLabel && '!ml-[10px] md:!ml-[14px]'
                  )}
                  iconWeight="bold"
                  className={c(
                    'ml-1.5 inline-flex items-baseline rounded p-0',
                    'text-base font-medium leading-6 tracking-tight text-blue',
                    'border-none bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent',
                    actionIcon &&
                      !microcopy.actionLabel &&
                      'h-5 w-5 [&_svg]:m-0 [&_svg]:h-full [&_svg]:w-full'
                  )}
                >
                  <span className="text-base font-medium text-white">
                    {microcopy.actionLabel}
                  </span>
                </Button.v2>
              </ToastPrimitive.Action>
            )}
          </div>
        </div>
        <ToastPrimitive.Close asChild>
          <Button.v2
            type="button"
            aria-label={microcopy.closeAriaLabel}
            variant="tertiary-black-monochrome"
            size="large"
            shape="square"
            onClick={handleCloseClick}
            iconRight={closeIcon}
            iconWeight="bold"
            iconClassName="text-white !w-5 !h-5"
            className={c(
              'flex h-12 w-12 items-center justify-center md:h-14 md:w-14',
              'rounded-none border-0 border-l border-grey-700',
              'bg-grey-900 hover:bg-grey-900 focus:bg-grey-900 active:bg-grey-900'
            )}
          />
        </ToastPrimitive.Close>
      </ToastPrimitive.Root>
      <ToastPrimitive.Viewport
        data-slot="snack-bar-viewport"
        className="fixed inset-x-0 bottom-4 z-50 flex justify-center px-4 md:bottom-5"
      />
    </ToastPrimitive.Provider>
  );
};
